/**
 * planosStore — Fonte única de verdade para os planos comerciais.
 *
 * Persistência:
 *  - S360: LocalStorage `s360_planos_v1` (CRUD via /admin/s360-planos)
 *  - V360: LocalStorage `v360_planos_v1` (CRUD via /admin/v360-planos — futuro;
 *           por enquanto usa seed padrão da planilha v11).
 *
 * Objetivo (Phase 11.12):
 *  - useS360Tier lê `limits` daqui (não mais constantes hardcoded).
 *  - NovoClientePlanos lê os preços/tiers daqui (não mais constantes hardcoded).
 *  - Alterações no Admin → refletem imediatamente na tela de contratação e
 *    nos limites operacionais do S360.
 */

const S360_LS = "s360_planos_v1";
const V360_LS = "v360_planos_v1";

// ─── SEEDS ──────────────────────────────────────────────────────────
// Seed S360 — planilha EMERGENT Sprints360_Planos_v11 (aba SPRINTS360 · Planos)
// Números derivados: limits operacionais (Phase 11.11).
// Phase 11.12c — desconto_anual_pct: aplicado sobre `mensal × 12` para
// obter `preco_anual` de forma dinâmica. Default 10%.
export const S360_SEED = [
  {
    id: "s360-start",
    nome: "Luoggo Projetos · START",
    classificacao: "START",
    descricao: "Para quem está começando a estruturar a execução — poucos projetos rodando, equipe pequena.",
    preco_mensal: 69,
    preco_anual: 745,
    desconto_anual_pct: 10,
    projetos_ativos_limite: "até 3 projetos",
    projetos_max: 3,
    usuarios_inclusos: "20 – 30",
    usuarios_max: 30,
    usuario_adicional_preco: 59,
    creditos_val_mes: "20 – 30",
    creditos_val_max: 30,
    atividades_max: 100,
    features: {
      kanban: true, prazos_dependencias: true, evolucao_recepcao: true,
      dashboard_progresso: true, val_consultor: true, integracoes_erp: false,
    },
    permite_consultoria: false,
    ativo: true,
  },
  {
    id: "s360-scale",
    nome: "Luoggo Projetos · SCALE",
    classificacao: "SCALE",
    descricao: "Vários projetos simultâneos, mais gente envolvida na execução do dia a dia.",
    preco_mensal: 199,
    preco_anual: 2149,
    desconto_anual_pct: 10,
    projetos_ativos_limite: "até 8 projetos",
    projetos_max: 8,
    usuarios_inclusos: "100 – 150",
    usuarios_max: 150,
    usuario_adicional_preco: 59,
    creditos_val_mes: "50 – 70",
    creditos_val_max: 70,
    atividades_max: 500,
    features: {
      kanban: true, prazos_dependencias: true, evolucao_recepcao: true,
      dashboard_progresso: true, val_consultor: true, integracoes_erp: false,
    },
    permite_consultoria: false,
    ativo: true,
  },
  {
    id: "s360-full",
    nome: "Luoggo Projetos · FULL",
    classificacao: "FULL",
    descricao: "Portfólio grande de iniciativas — precisa de visão consolidada e do Val sempre disponível para responder sobre qualquer projeto.",
    preco_mensal: 399,
    preco_anual: 4310,
    desconto_anual_pct: 10,
    projetos_ativos_limite: "até 14 projetos",
    projetos_max: 14,
    usuarios_inclusos: "250 – 300",
    usuarios_max: 300,
    usuario_adicional_preco: 59,
    creditos_val_mes: "50 – 70",
    creditos_val_max: 70,
    atividades_max: 1500,
    features: {
      kanban: true, prazos_dependencias: true, evolucao_recepcao: true,
      dashboard_progresso: true, val_consultor: true, integracoes_erp: false,
    },
    permite_consultoria: false,
    ativo: true,
  },
  {
    id: "s360-consulting",
    nome: "Luoggo Projetos · CONSULTING",
    classificacao: "CONSULTING",
    descricao: "Consultorias ou empresas que gerenciam múltiplos projetos de múltiplas empresas-cliente.",
    preco_mensal: 699,
    preco_anual: 7549,
    desconto_anual_pct: 10,
    projetos_ativos_limite: "até 20 projetos",
    projetos_max: 20,
    usuarios_inclusos: "250 – 300",
    usuarios_max: 300,
    usuario_adicional_preco: 59,
    creditos_val_mes: "50 – 70",
    creditos_val_max: 70,
    atividades_max: 3000,
    features: {
      kanban: true, prazos_dependencias: true, evolucao_recepcao: true,
      dashboard_progresso: true, val_consultor: true, integracoes_erp: false,
    },
    permite_consultoria: true,
    ativo: true,
  },
];

// Fallback numérico por classificação — usado durante migração de LS antigos
// que não têm os campos numéricos (`projetos_max`, `atividades_max`, etc.).
const S360_LIMITS_FALLBACK = {
  START:      { projetos_max: 3,  usuarios_max: 30,  atividades_max: 100,  creditos_val_max: 30, permite_consultoria: false },
  SCALE:      { projetos_max: 8,  usuarios_max: 150, atividades_max: 500,  creditos_val_max: 70, permite_consultoria: false },
  FULL:       { projetos_max: 14, usuarios_max: 300, atividades_max: 1500, creditos_val_max: 70, permite_consultoria: false },
  CONSULTING: { projetos_max: 20, usuarios_max: 300, atividades_max: 3000, creditos_val_max: 70, permite_consultoria: true  },
};

// Seed V360 — planilha v11 (aba V360 · Planos). Estrutura análoga ao S360.
export const V360_SEED = [
  {
    id: "v360-p1",
    nome: "Luoggo Gestão · P1",
    classificacao: "P1",
    descricao: "Porte P1 · pequena empresa. Avaliação de maturidade + Plano de Evolução.",
    preco_mensal: 69,
    preco_anual: 745,
    creditos_val_mes: "20 – 30",
    creditos_val_max: 30,
    ativo: true,
  },
  {
    id: "v360-p2",
    nome: "Luoggo Gestão · P2",
    classificacao: "P2",
    descricao: "Porte P2 · média empresa.",
    preco_mensal: 199,
    preco_anual: 2149,
    creditos_val_mes: "50 – 70",
    creditos_val_max: 70,
    ativo: true,
  },
  {
    id: "v360-p3",
    nome: "Luoggo Gestão · P3",
    classificacao: "P3",
    descricao: "Porte P3 · média empresa.",
    preco_mensal: 399,
    preco_anual: 4310,
    creditos_val_mes: "50 – 70",
    creditos_val_max: 70,
    ativo: true,
  },
  {
    id: "v360-p4",
    nome: "Luoggo Gestão · P4",
    classificacao: "P4",
    descricao: "Porte P4 · grande empresa · SLA prioritário.",
    preco_mensal: 699,
    preco_anual: 7549,
    creditos_val_mes: "50 – 70",
    creditos_val_max: 70,
    ativo: true,
  },
];

// ─── ACESSO A LOCAL STORAGE ────────────────────────────────────────
function safeRead(key, seed) {
  try {
    if (typeof window === "undefined") return seed;
    const raw = window.localStorage.getItem(key);
    if (!raw) return seed;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || !arr.length) return seed;
    return arr;
  } catch { return seed; }
}

/**
 * Calcula o preço anual S360 a partir de mensal + % desconto (default 10%).
 * `mensal × 12 × (1 - desc/100)`, arredondado a 2 casas.
 * Phase 11.12c — passa a ser a fonte-única do valor anual S360 nas telas de
 * contratação, ignorando o campo estático `preco_anual` do seed (deprecated).
 */
export function computeS360Anual(preco_mensal, desconto_anual_pct) {
  const m = Number(preco_mensal) || 0;
  const d = Number(desconto_anual_pct);
  const desc = Number.isFinite(d) ? d : 10;
  return Math.round(m * 12 * (1 - desc / 100) * 100) / 100;
}

/**
 * Normaliza um plano S360 do LS — garante que campos numéricos existam.
 * Se o admin editou preço/nome mas o LS foi criado antes da Phase 11.12,
 * pega o fallback pela `classificacao`.
 */
function normalizeS360(p) {
  const fb = S360_LIMITS_FALLBACK[p.classificacao] || S360_LIMITS_FALLBACK.FULL;
  const desc = Number.isFinite(p.desconto_anual_pct) ? p.desconto_anual_pct : 10;
  return {
    ...p,
    desconto_anual_pct: desc,
    // Phase 11.12c — anual sempre derivado do mensal × desconto (dinâmico)
    preco_anual: computeS360Anual(p.preco_mensal, desc),
    projetos_max:    Number.isFinite(p.projetos_max)    ? p.projetos_max    : fb.projetos_max,
    usuarios_max:    Number.isFinite(p.usuarios_max)    ? p.usuarios_max    : fb.usuarios_max,
    atividades_max:  Number.isFinite(p.atividades_max)  ? p.atividades_max  : fb.atividades_max,
    creditos_val_max: Number.isFinite(p.creditos_val_max) ? p.creditos_val_max : fb.creditos_val_max,
    permite_consultoria: typeof p.permite_consultoria === "boolean"
      ? p.permite_consultoria
      : fb.permite_consultoria,
  };
}

// ─── API PÚBLICA S360 ──────────────────────────────────────────────
export function getS360Planos() {
  return safeRead(S360_LS, S360_SEED).map(normalizeS360);
}

export function getS360ByTier(classificacao) {
  const p = getS360Planos().find(x => x.classificacao === classificacao);
  return p || null;
}

/** Retorna limits (projetos_max, usuarios_max, atividades_max, creditos_val_max, permite_consultoria) do tier. */
export function getS360Limits(classificacao) {
  const p = getS360ByTier(classificacao);
  const fb = S360_LIMITS_FALLBACK[classificacao] || S360_LIMITS_FALLBACK.FULL;
  if (!p) return fb;
  return {
    projetos_max:        p.projetos_max        ?? fb.projetos_max,
    usuarios_max:        p.usuarios_max        ?? fb.usuarios_max,
    atividades_max:      p.atividades_max      ?? fb.atividades_max,
    creditos_val_max:    p.creditos_val_max    ?? fb.creditos_val_max,
    permite_consultoria: p.permite_consultoria ?? fb.permite_consultoria,
  };
}

/**
 * Retorna o número comercial de "Usuários inclusos" do plano — o valor que
 * o admin preenche no campo `usuarios_inclusos` do CRUD (/admin/s360-planos).
 * Aceita valor plano ("4") ou faixa ("20 – 30"). Se for faixa, retorna o TETO
 * (último número), que é o padrão comercial. Se vazio, cai para `usuarios_max`.
 * Phase 11.13a — usado por Subscrições · Resumo dos Planos.
 */
export function parseUsuariosInclusos(planoOuStr) {
  if (planoOuStr == null) return 0;
  const src = typeof planoOuStr === "object" ? planoOuStr.usuarios_inclusos : planoOuStr;
  const s = String(src ?? "").trim();
  const nums = s.match(/\d+/g);
  if (nums && nums.length) return Number(nums[nums.length - 1]);
  if (typeof planoOuStr === "object") return Number(planoOuStr.usuarios_max) || 0;
  return 0;
}

// ─── API PÚBLICA V360 ──────────────────────────────────────────────
export function getV360Planos() {
  return safeRead(V360_LS, V360_SEED);
}

export function getV360ByPorte(classificacao) {
  const p = getV360Planos().find(x => x.classificacao === classificacao);
  return p || null;
}

// ─── LABELS ─────────────────────────────────────────────────────────
export const S360_TIER_LABELS = {
  START:      "Luoggo Projetos · START",
  SCALE:      "Luoggo Projetos · SCALE",
  FULL:       "Luoggo Projetos · FULL",
  CONSULTING: "Luoggo Projetos · CONSULTING",
};
